/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Notification } from 'types/entities/Notification'
import { Reminder } from 'types/entities/Reminder'
import { normalizeReminderModelName } from 'utils/reminders/reminderUtils'

export type NotificationEntityRef = {
    modelName: string
    modelId: number
    label?: string | null
}

export type NotificationLinkRef = {
    entity: NotificationEntityRef | null
    reminderId: number | null
}

const parseNotificationData = (
    data: Notification['data'] | string | Record<string, unknown> | null | undefined
): Record<string, unknown> | null => {
    if (!data) return null
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data)
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
        } catch {
            return null
        }
    }
    if (typeof data === 'object') {
        return data as Record<string, unknown>
    }
    return null
}

const toPositiveInt = (value: unknown): number | null => {
    if (value == null || value === '') return null
    const n = Number(value)
    return Number.isFinite(n) && n > 0 ? n : null
}

const resolveModelLabel = (reminder?: Reminder | null): string | null => {
    const model = reminder?.model as
        | {
              name?: string | null
              first_name?: string | null
              last_name?: string | null
              company_name?: string | null
          }
        | undefined
    if (!model) return null

    const fullName = [model.first_name, model.last_name]
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join(' ')
    const label = (model.name || model.company_name || fullName || '').trim()
    return label || null
}

const buildEntityRef = (
    rawName: unknown,
    rawId: unknown,
    label?: string | null
): NotificationEntityRef | null => {
    if (typeof rawName !== 'string' || !rawName.trim()) return null
    const modelId = toPositiveInt(rawId)
    if (!modelId) return null

    const modelName = normalizeReminderModelName(rawName) ?? rawName.toLowerCase()
    return { modelName, modelId, label: label || null }
}

export const getNotificationLinkRef = (
    notification: Notification | null | undefined
): NotificationLinkRef => {
    const data = parseNotificationData(notification?.data as any)
    const reminder = notification?.reminder ?? null

    // Reminder notifications put the entity on `reminder`, not in `data`.
    const entityFromReminder = buildEntityRef(
        reminder?.model_name,
        reminder?.model_id,
        resolveModelLabel(reminder)
    )
    const entityFromData = buildEntityRef(
        data?.model_name ?? data?.modelName ?? data?.entity_type ?? data?.entityType,
        data?.model_id ?? data?.modelId ?? data?.entity_id ?? data?.entityId
    )

    const reminderId =
        toPositiveInt(reminder?.id) ??
        toPositiveInt(data?.reminder_id) ??
        toPositiveInt(data?.reminderId) ??
        null

    return {
        entity: entityFromReminder ?? entityFromData,
        reminderId,
    }
}

/** @deprecated use getNotificationLinkRef */
export const getNotificationEntityRef = (
    notification: Notification | null | undefined
): NotificationEntityRef | null => getNotificationLinkRef(notification).entity
